/**
 * Narrow types for the `config.central.images` document, plus the pure helpers that read and rewrite it.
 *
 * The generated `src/http/protocol.ts` carries no type for this configuration, and the document
 * reaches the admin UI as raw JSON text rather than as a typed payload, so the shape is declared
 * here and the text is parsed directly. Keys outside this shape are not modelled but survive every
 * rewrite untouched.
 */

/** The style vocabulary the server accepts; anything outside it fails to deserialise there. */
export const THUMBNAIL_STYLES = ["main", "alternative", "promo", "lite", "originals"] as const;

export type ThumbnailStyle = (typeof THUMBNAIL_STYLES)[number];

export interface StyleSize {
  width: number;
  height: number;
}

type StyleSizes = Partial<Record<ThumbnailStyle, StyleSize>>;

export const FALLBACK_ORIENTATIONS = ["landscape", "portrait"] as const;

export type FallbackOrientation = (typeof FALLBACK_ORIENTATIONS)[number];

/** The operator's own stand-in artwork for games carrying no image in the enforced style, resolved per orientation. */
export type FallbackImages = Partial<Record<FallbackOrientation, string>>;

export interface CentralImagesGame {
  styles?: Partial<Record<ThumbnailStyle, string>>;
  "default-style"?: ThumbnailStyle;
  "style-sizes"?: StyleSizes;
  "fallback-images"?: FallbackImages;
}

export interface CentralImagesDocument {
  /** The feature's master switch, left untyped: `readCentralImagesEnabled` states what a non-boolean value means here. */
  enabled?: unknown;
  game?: CentralImagesGame;
}

export type EnforcedStyleSelection = { kind: "none" } | { kind: "style"; style: ThumbnailStyle; width: number; height: number };

const DEFAULT_STYLE_KEY = "default-style";
const STYLE_SIZES_KEY = "style-sizes";
const FALLBACK_IMAGES_KEY = "fallback-images";
const FALLBACK_INDENT = "  ";

function isThumbnailStyle(value: unknown): value is ThumbnailStyle {
  return typeof value === "string" && (THUMBNAIL_STYLES as readonly string[]).indexOf(value) !== -1;
}

/**
 * The record stores whole numbers, so a fraction is not a size the platform can keep at all.
 * `Number.isInteger` also turns away `NaN`, `Infinity`, and anything that is not a number, none of
 * which a range check on the result would catch.
 */
function isSizeValue(value: unknown): value is number {
  return Number.isInteger(value);
}

function isStyleSize(value: unknown): value is StyleSize {
  const size = value as Partial<StyleSize> | undefined;
  return isSizeValue(size?.width) && isSizeValue(size?.height);
}

/**
 * The editor hands over whatever the operator has typed so far, so an unparseable document is an
 * ordinary intermediate state rather than an error worth throwing over.
 */
export function parseCentralImagesDocument(source: string): CentralImagesDocument | null {
  try {
    const parsed: unknown = JSON.parse(source);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    // JSON cannot promise that the modelled keys hold the modelled types, so every reader below
    // guards the value it branches on rather than trusting this assertion.
    return parsed as CentralImagesDocument;
  } catch {
    return null;
  }
}

/** In the vocabulary's own order rather than the document's, and a blank template is treated as no template, matching how the server resolves one. */
function listStylesWithTemplate(centralImages: CentralImagesDocument | null): ThumbnailStyle[] {
  const templates = centralImages?.game?.styles;
  if (templates === undefined) {
    return [];
  }
  return THUMBNAIL_STYLES.filter((style) => {
    const template = templates[style];
    return typeof template === "string" && template.trim() !== "";
  });
}

/**
 * A value that is not a boolean is the server's to report, and treating it as "not false" keeps the
 * admin UI from claiming the feature is off on the strength of a value it does not understand.
 */
export function readCentralImagesEnabled(centralImages: CentralImagesDocument | null): boolean | undefined {
  const enabled = centralImages?.enabled;
  return typeof enabled === "boolean" ? enabled : undefined;
}

export function readDefaultStyle(centralImages: CentralImagesDocument | null): ThumbnailStyle | undefined {
  const defaultStyle = centralImages?.game?.[DEFAULT_STYLE_KEY];
  return isThumbnailStyle(defaultStyle) ? defaultStyle : undefined;
}

export interface StyleOption {
  style: ThumbnailStyle;
  /** Whether the style has a url template, without which the server ignores it. */
  usable: boolean;
}

/**
 * A `default-style` the server ignores for want of a template is precisely the state this control
 * exists to explain, so hiding it would leave the picker blank on that document and put the style
 * out of reach.
 */
export function toStyleOptions(centralImages: CentralImagesDocument | null, selected: ThumbnailStyle | undefined): StyleOption[] {
  const options: StyleOption[] = listStylesWithTemplate(centralImages).map((style) => ({ style, usable: true }));
  [readDefaultStyle(centralImages), selected].forEach((style) => {
    if (style !== undefined && !options.some((option) => option.style === style)) {
      options.push({ style, usable: false });
    }
  });
  return options;
}

/**
 * A half-written entry is an ordinary intermediate state of a document being typed, and reading one
 * as a size would call a style enforced that the server ignores.
 */
export function readStyleSize(centralImages: CentralImagesDocument | null, style: ThumbnailStyle): StyleSize | undefined {
  const size = centralImages?.game?.[STYLE_SIZES_KEY]?.[style];
  return isStyleSize(size) ? size : undefined;
}

export function readStyleTemplate(centralImages: CentralImagesDocument | null, style: ThumbnailStyle): string | undefined {
  const template = centralImages?.game?.styles?.[style];
  return typeof template === "string" ? template : undefined;
}

/** A blank url names no image, the way a blank template names no template, so it reads as an orientation left unset. */
function readFallbackUrl(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/**
 * Both orientations, in a fixed key order so the result reads the same for documents that differ
 * only in the order they wrote the pair.
 */
export function readFallbackImages(centralImages: CentralImagesDocument | null): FallbackImages {
  const fallbackImages: unknown = centralImages?.game?.[FALLBACK_IMAGES_KEY];
  if (typeof fallbackImages !== "object" || fallbackImages === null || Array.isArray(fallbackImages)) {
    return {};
  }
  const pair = fallbackImages as FallbackImages;
  return Object.fromEntries(FALLBACK_ORIENTATIONS.map((orientation) => [orientation, readFallbackUrl(pair[orientation])]));
}

/** The basis of a document that cannot be read, which no readable document shares. */
const UNREADABLE_BASIS = "unreadable";

/**
 * A signature of the projection an editing state is formed against. Two documents that share a
 * basis are the same document to that state; the bodies that cannot be read all answer with one
 * basis of their own, so unreadability tells them from a readable document but not from each other.
 *
 * The templates' own text is left out, along with every other key. The document reaches this control
 * as raw text the operator types into, so a basis that moved with the text would discard a size
 * being typed here on every keystroke made there. A retyped url does invalidate the reading taken
 * through it, which is why a reading is pinned on what it was taken of and not on this.
 */
export function documentBasis(centralImages: CentralImagesDocument | null): string {
  if (centralImages === null) {
    return UNREADABLE_BASIS;
  }
  const sizes = THUMBNAIL_STYLES.map((style) => {
    const size = readStyleSize(centralImages, style);
    return size === undefined ? "" : `${size.width}x${size.height}`;
  });
  return JSON.stringify([readDefaultStyle(centralImages) ?? "", listStylesWithTemplate(centralImages), sizes]);
}

/**
 * The basis the fallback-url draft is formed against: the urls themselves and nothing else.
 *
 * Deliberately narrower than `documentBasis` rather than built on it. The two controls edit
 * different keys of one document, and a basis that moved with the other control's keys would
 * discard the url being typed here every time a size was applied there.
 */
export function fallbackImagesBasis(centralImages: CentralImagesDocument | null): string {
  if (centralImages === null) {
    return UNREADABLE_BASIS;
  }
  return JSON.stringify(readFallbackImages(centralImages));
}

/**
 * A rewrite hands the operator back the file they were editing rather than a reformatted one. JSON
 * forbids a raw newline inside a string, so the first indented line is always structural.
 */
function detectIndent(source: string): string {
  const firstIndentedLine = /\n([ \t]+)\S/.exec(source);
  return firstIndentedLine === null ? FALLBACK_INDENT : firstIndentedLine[1];
}

/**
 * Parsing asserts the modelled shape rather than checking it, so `game` may be any JSON value; one
 * that cannot hold keys is left as it stands for the server to report on.
 */
function readGameObject(centralImages: CentralImagesDocument): CentralImagesGame | undefined {
  const game: unknown = centralImages.game;
  return typeof game === "object" && game !== null && !Array.isArray(game) ? (game as CentralImagesGame) : undefined;
}

/**
 * Spreading a value that is not an object would turn a hand-edited `"style-sizes": "abc"` into one
 * entry per character, inventing sizes the operator never wrote. Carrying nothing forward discards
 * it instead, which is what recording the chosen size costs: `style-sizes` is the only place the
 * document can hold a size, so a value there that cannot hold one has to give way.
 */
function readSizeEntries(game: CentralImagesGame): StyleSizes | undefined {
  const sizes: unknown = game[STYLE_SIZES_KEY];
  return typeof sizes === "object" && sizes !== null && !Array.isArray(sizes) ? (sizes as StyleSizes) : undefined;
}

/**
 * Spreading a value that cannot hold keys would scatter a hand-edited `"fallback-images": "abc"`
 * across one entry per character, so such a value is replaced outright rather than merged into.
 */
function readFallbackEntries(game: CentralImagesGame): FallbackImages | undefined {
  const pair: unknown = game[FALLBACK_IMAGES_KEY];
  return typeof pair === "object" && pair !== null && !Array.isArray(pair) ? (pair as FallbackImages) : undefined;
}

/**
 * The document with only `game.fallback-images` changed, on the terms `writeEnforcedStyle` sets out
 * below.
 *
 * An orientation left unset loses its key rather than being written empty — the whole
 * `fallback-images` key when that leaves neither behind. The server warns the operator that elements
 * may have gone missing whenever a saved document round-trips to something other than what they
 * sent, so a key added to hold nothing costs them that warning on a document they meant to clear.
 */
export function writeFallbackImages(source: string, selection: FallbackImages): string | null {
  const centralImages = parseCentralImagesDocument(source);
  if (centralImages === null) {
    return null;
  }

  const chosen = FALLBACK_ORIENTATIONS.map((orientation) => [orientation, readFallbackUrl(selection[orientation])?.trim()] as const);

  if (chosen.every(([, url]) => url === undefined)) {
    const existing = readGameObject(centralImages);
    if (existing !== undefined) {
      delete existing[FALLBACK_IMAGES_KEY];
    }
  } else {
    const game: CentralImagesGame = readGameObject(centralImages) ?? {};
    const pair: FallbackImages = { ...readFallbackEntries(game) };
    chosen.forEach(([orientation, url]) => {
      if (url === undefined) {
        delete pair[orientation];
      } else {
        pair[orientation] = url;
      }
    });
    game[FALLBACK_IMAGES_KEY] = pair;
    centralImages.game = game;
  }

  const trailingWhitespace = /\s*$/.exec(source);
  return JSON.stringify(centralImages, null, detectIndent(source)) + (trailingWhitespace === null ? "" : trailingWhitespace[0]);
}

/**
 * The document with only `game.default-style` and the chosen style's `game.style-sizes` entry changed.
 *
 * Every other key — including the size entries of styles the operator is not choosing — keeps its
 * value and its position, because this control edits one corner of a document whose remaining keys
 * are owned elsewhere. The text is re-emitted from the parsed document at the indentation the
 * source uses, so a source already in that canonical shape comes back byte-identical apart from
 * the two edits, while hand formatting a serializer cannot express — inline arrays, blank lines,
 * padding around colons — is normalised. Clearing the selection removes the `default-style` key and
 * leaves the recorded sizes in place, so re-choosing that style does not make the operator measure
 * it again.
 */
export function writeEnforcedStyle(source: string, selection: EnforcedStyleSelection): string | null {
  const centralImages = parseCentralImagesDocument(source);
  if (centralImages === null) {
    return null;
  }

  if (selection.kind === "none") {
    const existing = readGameObject(centralImages);
    if (existing !== undefined) {
      delete existing[DEFAULT_STYLE_KEY];
    }
  } else {
    const game: CentralImagesGame = readGameObject(centralImages) ?? {};
    game[DEFAULT_STYLE_KEY] = selection.style;
    game[STYLE_SIZES_KEY] = {
      ...readSizeEntries(game),
      [selection.style]: { width: selection.width, height: selection.height },
    };
    centralImages.game = game;
  }

  const trailingWhitespace = /\s*$/.exec(source);
  return JSON.stringify(centralImages, null, detectIndent(source)) + (trailingWhitespace === null ? "" : trailingWhitespace[0]);
}
